import { Button, ColorTypes, dom } from "@omegagrid/core";
import { CellModel, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { msg } from "@omegagrid/localize";
import { GridEditorAdapter, GridEditorOptions, RequestParamsOrder, RowData } from "@reporting/rp-app-host";
import rp from "@reporting/rp-app-host";
import { Parameter, Query } from "@reporting/rp-types/ext";

const DEFAULT_FIELDS = ['idz', 'name', 'ord_num', 'data_type', 'id_thread', 'global', 'default_value_sql', 'default_value'];
const DEFAULT_ORDER: RequestParamsOrder = [['ord_num', 'asc'], ['name', 'asc']];

const sqlColumnRenderer: (div: HTMLDivElement, cell: CellModel) => HTMLDivElement = (div, cell) => {
	dom.empty(div);
	div.style.padding = '0 2px';
	div.style.display = 'flex';
	div.style.alignItems = 'flex-start';
	div.style.gap = '4px';
	div.appendChild(dom.createElement<Button>('og-button', {
		color: ColorTypes.Gray,
		icon: 'pencil',
		title: 'SQL',
		style: {height: '16px', display: 'block', flex: '0 0 16px', marginTop: '2px'},
		listeners: [
			['pointermove', (e: PointerEvent) => e.stopPropagation()],
			['pointerdown', (e: PointerEvent) => e.stopPropagation()],
			['click', (e: PointerEvent) => {
				e.stopPropagation();
				rp.host.open(`extensions-parameterValue-${cell.row.data.idz}`, {
					title: cell.row.data.name
				});
			}]
		]
	}));
	div.appendChild(dom.createElement<HTMLDivElement>('div', {
		innerText: cell.value as string || '',
		style: {flex: '1', overflow: 'hidden', textOverflow: 'ellipsis'},
	}));
	return div;
};

export class ParamGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _rows: RowSource[];

	constructor(private idProject: number) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;

		// only queries of the current project - a parameter must not be moved to another one
		const queries = (await rp.ms('extensions').get<Query[]>(`queries`, {
			fields: ['id_thread', 'description'],
			where: {id_custom_page: this.idProject},
			order: [['description', 'asc']]
		}));

		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'delete',
				icon: 'trash',
				color: ColorTypes.Red,
				title: msg('Delete')
			}, {
				action: 'duplicate',
				icon: 'copy',
				color: ColorTypes.Yellow,
				title: msg('Duplicate')
			}],
			filter: false
		}, {
			data: 'id_thread',
			header: msg('Query'),
			t: 's',
			e: true,
			content: queries.map(q => ({k: q.id_thread, v: q.description})),
			w: 150
		}, {
			data: 'name',
			header: msg('Name'),
			t: 't',
			e: true,
			w: 150
		}, {
			data: 'ord_num',
			header: msg('Order'),
			t: 'n',
			e: true,
			w: 50
		}, {
			data: 'data_type',
			header: msg('Data Type'),
			t: 't',
			e: true,
			w: 200
		},{
			data: 'global',
			header: msg('Global'),
			t: 'b',
			e: true,
			w: 60
		}, {
			data: 'default_value_type',
			header: msg('Default Value Type'),
			t: 's',
			e: true,
			w: 140,
			content: [
				{k: '0', v: msg('constant')},
				{k: '1', v: msg('sql')}
			]
		}, {
			data: 'default_value',
			header: msg('Default Value'),
			t: 't',
			e: true,
			w: 200
		}];

		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		if (this._rows) return this._rows;

		this._rows = (await rp.ms('extensions').get<Parameter[]>(
			`projects/${this.idProject}/parameters`,
			this.createRequestParams(filters, {
				fields: DEFAULT_FIELDS,
				order: DEFAULT_ORDER,
				count: this.pageSize
			})
		)).map(data => ({data: {default_value_type: data.default_value_sql ? '1' : '0', ...data}}));

		return this._rows;
	}

	getCells() {
		const defaultValueColIndex = this._columns.findIndex(col => col.data === 'default_value');
		const cells = this._rows.map((row, r) => ({
			r: r,
			c: defaultValueColIndex,
			renderer: row.data.default_value_type === '1' ? sqlColumnRenderer : null
		}));

		this._rows = null;
		return cells;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: this.pageSize,
			refreshAfterSave: false,
		});
	}

	async save(rowData: RowData[]) {
		this._rows = null;
		const data = rowData.map(row => Object.assign({
			_state: row.state,
			_index: row.index,
			default_value_sql: row.data.default_value_type === '1'
		}, row.data));
		await rp.ms('extensions').post(`parameters`, data);
	}

}
